import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useModalConfirm,
  usePageContext,
  usePassengersBulkActions,
  useSelectedIds,
  useYupValidationResolver,
} from "@ryde/redux";
import { ConfirmResult, DrawerType, useDrawers } from "@ryde/shared";

import { BulkActivateShiftTemplateFormFieldsEnum } from "../models";
import { useBulkActivateShiftTemplateValidation } from "./use-bulk-passenger-activate-shift-template.validation";

export interface IBulkActivateShiftTemplateForm {
  startDate: TDateISO;
}

export const useBulkActivateShiftTemplate = () => {
  const { entity } = usePageContext();
  const { selectedRowsIds } = useSelectedIds("passenger");
  const { bulkActivateShiftTemplate } = usePassengersBulkActions();
  const { validation } = useBulkActivateShiftTemplateValidation();
  const { confirmDialogResult } = useModalConfirm();
  const { closeDrawer } = useDrawers();
  const { t } = useTranslation();

  const resolver = useYupValidationResolver(validation, entity);

  const methods = useForm<IBulkActivateShiftTemplateForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver,
    defaultValues: {
      startDate: dayjs().add(1, "day").startOf("day").toISOString(),
    },
  });

  const { getValues, trigger } = methods;

  const handleActivate = useEvent(async () => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.BULK_ACTIVATE_SHIFT_TEMPLATE_MESSAGE"),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const startDateValue = getValues(
      BulkActivateShiftTemplateFormFieldsEnum.startDate,
    );

    const result = await bulkActivateShiftTemplate({
      ids: selectedRowsIds,
      startDate: dayjs(startDateValue).toISOString(),
    });

    if (result?.success) {
      closeDrawer(DrawerType.BulkActivateShiftTemplate);
    }
  });

  return {
    methods,
    handleActivate,
  };
};
